import React, { useCallback, useEffect, useRef, useState } from "react";
import { CommentOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFeedback } from "./FeedbackContext";

const PULSE_DISMISS_KEY = "verdictia.feedback.dock.pulseDismissed";

/**
 * Dock de feedback global, ancré bas-droite.
 * - pastille au repos, pill au hover (largeur animée)
 * - pulse idle subtil tant que l'utilisateur n'a jamais cliqué
 * - raccourci clavier "?" pour ouvrir (ignoré si focus dans un input/textarea)
 */
const FeedbackDock = ({ hidden = false }) => {
    const { t } = useTranslation("review");
    const { openFeedback } = useFeedback();
    const [hovered, setHovered] = useState(false);
    const [pulseEnabled, setPulseEnabled] = useState(() => {
        try {
            return window.localStorage.getItem(PULSE_DISMISS_KEY) !== "1";
        } catch {
            return true;
        }
    });
    const buttonRef = useRef(null);

    const dismissPulse = useCallback(() => {
        if (!pulseEnabled) return;
        setPulseEnabled(false);
        try {
            window.localStorage.setItem(PULSE_DISMISS_KEY, "1");
        } catch {
            /* noop */
        }
    }, [pulseEnabled]);

    const handleOpen = useCallback(() => {
        dismissPulse();
        openFeedback({
            featureArea: "global_review",
            targetType: "review_page",
            badgeLabel: null,
            contextJson: { source: "feedback_dock" },
        });
    }, [dismissPulse, openFeedback]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== "?" && !(e.key === "/" && (e.metaKey || e.ctrlKey))) return;
            const target = e.target;
            const tag = target?.tagName;
            const editable = target?.isContentEditable;
            if (editable || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
            e.preventDefault();
            handleOpen();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [handleOpen]);

    if (hidden) return null;

    return (
        <button
            ref={buttonRef}
            type="button"
            className={`feedback-dock ${hovered ? "is-hovered" : ""} ${pulseEnabled ? "is-pulsing" : ""}`}
            onClick={handleOpen}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setHovered(true)}
            onBlur={() => setHovered(false)}
            aria-label={t("feedback.open")}
            title={`${t("feedback.open")}  ·  ?`}
        >
            <span className="feedback-dock__icon" aria-hidden="true">
                <CommentOutlined />
            </span>
            <span className="feedback-dock__label">{t("feedback.open")}</span>
        </button>
    );
};

export default FeedbackDock;

